/**
 * W1-11a (app-registry-keymap): chord grammar and normalisation (ARCHITECTURE.md §6.3, DESIGN.md §8.1).
 *
 * Grammar (canonical form, see `Chord` in ./types.ts):
 *   [hold:][Ctrl+][Alt+][Shift+]<code>   physical key (KeyboardEvent.code); `hold:` = fire on down and on up
 *   [Ctrl+][Alt+][Shift+]Wheel           mouse wheel / trackpad gesture
 *   [Ctrl+][Alt+]char:<character>        produced character (Shift is implied by the character)
 * `parseChord` is tolerant (any modifier order and case, aliases such as `Cmd`, `Option`, `K`, `1`, `Esc`, `Up`,
 * `,`), `formatChord` always writes the canonical form. On macOS `Ctrl` means Cmd: events with metaKey produce
 * `Ctrl+…`, events with the physical Control key produce no chord (Ctrl+click is a right-click there); on other
 * systems the Windows/Super key produces no chord. AltGr (getModifierState('AltGraph')) never produces a code chord,
 * so Ctrl+Alt bindings never match while the user types a character with AltGr.
 * Wheel deltas are normalised to pixels (deltaMode lines/pages) and scaled by the scroll sensitivity.
 */

import { WHEEL_NOTCH_PX, wheelDeltaPx } from '../../core/util/wheel';
import type { Chord } from './types';

export type Os = 'mac' | 'other';

export interface ParsedChord {
  readonly kind: 'key' | 'wheel' | 'char';
  /** Press-and-hold binding (`hold:` prefix): runs on keydown, releases on keyup. Keys only. */
  readonly hold: boolean;
  readonly ctrl: boolean;
  readonly alt: boolean;
  /** Always false for `char` chords (the character implies it). */
  readonly shift: boolean;
  /** KeyboardEvent.code for 'key', the character for 'char', '' for 'wheel'. */
  readonly key: string;
}

// ---------------------------------------------------------------------------------------------------------------
// Key codes
// ---------------------------------------------------------------------------------------------------------------

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const DIGITS = '0123456789'.split('');

/** Codes whose label depends on the keyboard layout (they produce a character). */
export const LAYOUT_CODES: ReadonlySet<string> = new Set([
  ...LETTERS.map((l) => `Key${l}`),
  ...DIGITS.map((d) => `Digit${d}`),
  'Minus',
  'Equal',
  'BracketLeft',
  'BracketRight',
  'Backslash',
  'Semicolon',
  'Quote',
  'Backquote',
  'Comma',
  'Period',
  'Slash',
  'IntlBackslash',
  'IntlRo',
  'IntlYen',
]);

/** Named (layout-independent) keys that can be bound. */
export const NAMED_CODES: ReadonlySet<string> = new Set([
  'Space',
  'Enter',
  'Escape',
  'Tab',
  'Backspace',
  'Delete',
  'Insert',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ContextMenu',
  'Pause',
  'PrintScreen',
]);

export const FUNCTION_CODES: ReadonlySet<string> = new Set(Array.from({ length: 24 }, (_, i) => `F${String(i + 1)}`));

export const NUMPAD_CODES: ReadonlySet<string> = new Set([
  ...DIGITS.map((d) => `Numpad${d}`),
  'NumpadAdd',
  'NumpadSubtract',
  'NumpadMultiply',
  'NumpadDivide',
  'NumpadDecimal',
  'NumpadEqual',
  'NumpadComma',
]);

/** Every code a chord may use (NumpadEnter is folded into Enter). */
export const KNOWN_CODES: ReadonlySet<string> = new Set([
  ...LAYOUT_CODES,
  ...NAMED_CODES,
  ...FUNCTION_CODES,
  ...NUMPAD_CODES,
]);

/** `KeyboardEvent.key` values of bare modifier presses (never a chord on their own). */
const MODIFIER_KEYS: ReadonlySet<string> = new Set([
  'Control',
  'Shift',
  'Alt',
  'AltGraph',
  'Meta',
  'OS',
  'Super',
  'Hyper',
  'Fn',
  'FnLock',
  'CapsLock',
  'NumLock',
  'ScrollLock',
  'Symbol',
  'SymbolLock',
]);

const MODIFIER_CODES: ReadonlySet<string> = new Set([
  'ControlLeft',
  'ControlRight',
  'ShiftLeft',
  'ShiftRight',
  'AltLeft',
  'AltRight',
  'MetaLeft',
  'MetaRight',
  'OSLeft',
  'OSRight',
  'CapsLock',
  'NumLock',
  'ScrollLock',
  'Fn',
  'FnLock',
]);

/** Aliases accepted by parseChord (lower case) -> canonical code. */
const KEY_ALIASES: ReadonlyMap<string, string> = new Map<string, string>([
  ...LETTERS.map((l): [string, string] => [l.toLowerCase(), `Key${l}`]),
  ...DIGITS.map((d): [string, string] => [d, `Digit${d}`]),
  ['esc', 'Escape'],
  ['escape', 'Escape'],
  ['del', 'Delete'],
  ['delete', 'Delete'],
  ['ins', 'Insert'],
  ['insert', 'Insert'],
  ['return', 'Enter'],
  ['enter', 'Enter'],
  ['numpadenter', 'Enter'],
  ['space', 'Space'],
  ['spacebar', 'Space'],
  ['tab', 'Tab'],
  ['backspace', 'Backspace'],
  ['home', 'Home'],
  ['end', 'End'],
  ['pageup', 'PageUp'],
  ['pgup', 'PageUp'],
  ['pagedown', 'PageDown'],
  ['pgdn', 'PageDown'],
  ['up', 'ArrowUp'],
  ['down', 'ArrowDown'],
  ['left', 'ArrowLeft'],
  ['right', 'ArrowRight'],
  [',', 'Comma'],
  ['.', 'Period'],
  ['/', 'Slash'],
  [';', 'Semicolon'],
  ["'", 'Quote'],
  ['`', 'Backquote'],
  ['[', 'BracketLeft'],
  [']', 'BracketRight'],
  ['\\', 'Backslash'],
  ['-', 'Minus'],
  ['=', 'Equal'],
]);

const CODE_BY_LOWER: ReadonlyMap<string, string> = new Map([...KNOWN_CODES].map((c) => [c.toLowerCase(), c]));

/** Canonical code for a code name or alias (case-insensitive), or null. */
export function canonicalCode(name: string): string | null {
  if (name === '') return null;
  const lower = name.toLowerCase();
  return CODE_BY_LOWER.get(lower) ?? KEY_ALIASES.get(lower) ?? null;
}

// ---------------------------------------------------------------------------------------------------------------
// Parsing and formatting
// ---------------------------------------------------------------------------------------------------------------

type Modifier = 'ctrl' | 'alt' | 'shift';

const MODIFIER_TOKENS: ReadonlyMap<string, Modifier> = new Map<string, Modifier>([
  ['ctrl', 'ctrl'],
  ['control', 'ctrl'],
  ['cmd', 'ctrl'],
  ['command', 'ctrl'],
  ['meta', 'ctrl'],
  ['mod', 'ctrl'],
  ['primary', 'ctrl'],
  ['alt', 'alt'],
  ['option', 'alt'],
  ['opt', 'alt'],
  ['shift', 'shift'],
]);

function parseModifiers(tokens: readonly string[]): { ctrl: boolean; alt: boolean; shift: boolean } | null {
  const mods = { ctrl: false, alt: false, shift: false };
  for (const raw of tokens) {
    const mod = MODIFIER_TOKENS.get(raw.trim().toLowerCase());
    if (mod === undefined || mods[mod]) return null;
    mods[mod] = true;
  }
  return mods;
}

/** Number of code points (good enough to recognise a single produced character). */
export function codePointLength(s: string): number {
  let n = 0;
  for (const _cp of s) n++;
  return n;
}

/** Tolerant parser: modifiers in any order and case, key aliases; null when the text is not a chord. */
export function parseChord(text: string): ParsedChord | null {
  let rest = text.trim();
  if (rest === '') return null;
  let hold = false;
  if (rest.toLowerCase().startsWith('hold:')) {
    hold = true;
    rest = rest.slice(5).trim();
  }
  const charAt = rest.toLowerCase().indexOf('char:');
  if (charAt >= 0) {
    if (hold) return null;
    const head = rest.slice(0, charAt);
    const ch = rest.slice(charAt + 5);
    if (codePointLength(ch) !== 1 || ch.trim() === '') return null;
    if (head !== '' && !head.endsWith('+')) return null;
    const mods = parseModifiers(head === '' ? [] : head.slice(0, -1).split('+'));
    if (mods === null) return null;
    return { kind: 'char', hold: false, ctrl: mods.ctrl, alt: mods.alt, shift: false, key: ch };
  }
  const parts = rest.split('+');
  // A trailing '+' means the key itself was '+' (not bindable by code): reject.
  const keyToken = parts.pop()?.trim() ?? '';
  const mods = parseModifiers(parts);
  if (mods === null || keyToken === '') return null;
  if (keyToken.toLowerCase() === 'wheel') {
    if (hold) return null;
    return { kind: 'wheel', hold: false, ...mods, key: '' };
  }
  const code = canonicalCode(keyToken);
  if (code === null) return null;
  return { kind: 'key', hold, ...mods, key: code };
}

/** Canonical text of a parsed chord. */
export function formatChord(p: ParsedChord): Chord {
  const mods = `${p.ctrl ? 'Ctrl+' : ''}${p.alt ? 'Alt+' : ''}`;
  if (p.kind === 'char') return `${mods}char:${p.key}`;
  const body = `${mods}${p.shift ? 'Shift+' : ''}${p.kind === 'wheel' ? 'Wheel' : p.key}`;
  return p.hold && p.kind === 'key' ? `hold:${body}` : body;
}

/** Canonical form of a chord, or null when it is not valid. */
export function normalizeChord(chord: string): Chord | null {
  const p = parseChord(chord);
  return p === null ? null : formatChord(p);
}

/** The chord as Playwright's `keyboard.press` text ('Ctrl+Shift+KeyA' -> 'Control+Shift+KeyA'; Cmd on macOS). */
export function pressText(chord: Chord, os: Os): string {
  const p = parseChord(chord);
  if (p === null) return chord;
  const mods: string[] = [];
  if (p.ctrl) mods.push(os === 'mac' ? 'Meta' : 'Control');
  if (p.alt) mods.push('Alt');
  if (p.shift) mods.push('Shift');
  return [...mods, p.key].join('+');
}

/** The key combination that triggers a chord (canonical, without `hold:`); null when invalid. */
export function triggerOf(chord: string): Chord | null {
  const p = parseChord(chord);
  return p === null ? null : formatChord({ ...p, hold: false });
}

export function isHoldChord(chord: string): boolean {
  return parseChord(chord)?.hold ?? false;
}

export function chordHasAlt(chord: string): boolean {
  return parseChord(chord)?.alt ?? false;
}

/** Ctrl+Alt chords are never bound (AltGr on European layouts). */
export function isCtrlAltChord(chord: string): boolean {
  const p = parseChord(chord);
  return p !== null && p.ctrl && p.alt;
}

// ---------------------------------------------------------------------------------------------------------------
// Platform
// ---------------------------------------------------------------------------------------------------------------

interface NavigatorLike {
  readonly platform?: string;
  readonly userAgent?: string;
  readonly userAgentData?: { readonly platform?: string };
}

/** macOS (and iPadOS with a keyboard) use Cmd as the primary modifier. */
export function detectOs(
  nav: NavigatorLike | undefined = typeof navigator === 'undefined' ? undefined : navigator,
): Os {
  if (nav === undefined) return 'other';
  const platform = nav.userAgentData?.platform ?? nav.platform ?? '';
  if (/mac|iphone|ipad|ipod/i.test(platform)) return 'mac';
  return /Macintosh|Mac OS X|iPhone|iPad/.test(nav.userAgent ?? '') ? 'mac' : 'other';
}

// ---------------------------------------------------------------------------------------------------------------
// Keyboard events
// ---------------------------------------------------------------------------------------------------------------

type KeyEventLike = Pick<
  KeyboardEvent,
  'key' | 'code' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'metaKey' | 'isComposing'
> & {
  getModifierState?: (key: string) => boolean;
};

/** AltGr held (it produces characters, never a code chord). */
export function altGraph(e: { getModifierState?: (key: string) => boolean }): boolean {
  try {
    return e.getModifierState?.('AltGraph') ?? false;
  } catch {
    return false;
  }
}

/** True for a press of a modifier key alone (never a chord). */
export function isBareModifier(e: Pick<KeyboardEvent, 'key' | 'code'>): boolean {
  return MODIFIER_KEYS.has(e.key) || MODIFIER_CODES.has(e.code);
}

function composing(e: KeyEventLike): boolean {
  return e.isComposing || e.key === 'Process';
}

/** A printable, non-blank single character produced by the event, or null. */
function producedChar(e: KeyEventLike): string | null {
  return codePointLength(e.key) === 1 && e.key.trim() !== '' ? e.key : null;
}

/**
 * Both candidate chords of a keyboard event: the physical-code chord (null for bare modifiers, IME composition,
 * AltGr, a foreign modifier or an unknown code) and the produced-character chord (`char:?`).
 */
export function eventChords(e: KeyEventLike, os: Os): { readonly code: Chord | null; readonly char: Chord | null } {
  if (composing(e) || isBareModifier(e)) return { code: null, char: null };
  // The physical Control key on macOS and the Windows/Super key elsewhere are not part of the grammar.
  const foreign = os === 'mac' ? e.ctrlKey : e.metaKey;
  if (foreign) return { code: null, char: null };
  const primary = os === 'mac' ? e.metaKey : e.ctrlKey;
  const ag = altGraph(e);
  const ch = producedChar(e);
  const char =
    ch === null
      ? null
      : formatChord({
          kind: 'char',
          hold: false,
          ctrl: ag ? false : primary,
          alt: ag ? false : e.altKey,
          shift: false,
          key: ch,
        });
  if (ag) return { code: null, char };
  const code = e.code === 'NumpadEnter' ? 'Enter' : e.code;
  if (!KNOWN_CODES.has(code)) return { code: null, char };
  return {
    code: formatChord({ kind: 'key', hold: false, ctrl: primary, alt: e.altKey, shift: e.shiftKey, key: code }),
    char,
  };
}

/**
 * KeymapService.chordOf: the physical chord of a keyboard event; for keys without a usable code (virtual
 * keyboards) the produced-character chord; null for bare modifiers, IME composition, AltGr and foreign modifiers.
 */
export function chordOfEvent(e: KeyEventLike, os: Os): Chord | null {
  const c = eventChords(e, os);
  if (c.code !== null) return c.code;
  return e.code === '' || !KNOWN_CODES.has(e.code) ? c.char : null;
}

// ---------------------------------------------------------------------------------------------------------------
// Wheel
// ---------------------------------------------------------------------------------------------------------------

type WheelEventLike = Pick<
  WheelEvent,
  'deltaX' | 'deltaY' | 'deltaMode' | 'ctrlKey' | 'altKey' | 'shiftKey' | 'metaKey'
>;

/** Wheel chord: on macOS both Cmd and the trackpad pinch (ctrlKey) count as `Ctrl` (zoom). */
export function wheelChord(e: WheelEventLike, os: Os): Chord {
  const ctrl = os === 'mac' ? e.ctrlKey || e.metaKey : e.ctrlKey;
  return formatChord({ kind: 'wheel', hold: false, ctrl, alt: e.altKey, shift: e.shiftKey, key: '' });
}

/** Normalised wheel deltas passed to wheel-bound commands as `args.wheel`. */
export interface WheelInfo {
  /** Horizontal delta in CSS pixels. */
  readonly dx: number;
  /** Vertical delta in CSS pixels (positive = towards the user / down). */
  readonly dy: number;
  /** Vertical delta in wheel notches (100 px each). */
  readonly notches: number;
  /** Beats to scroll: dy / 100 * settings.editing.scrollSensitivity. */
  readonly beats: number;
  /** ctrlKey wheel: a trackpad pinch or Ctrl+wheel; both zoom time. */
  readonly pinch: boolean;
  /** A horizontal gesture (two-finger swipe or tilt wheel): pans the lane X. */
  readonly horizontal: boolean;
}

/** Pixels per line (deltaMode 1) and per notch: shared with the timeline host (core/util/wheel.ts). */
export { WHEEL_LINE_PX, WHEEL_NOTCH_PX } from '../../core/util/wheel';

export function normalizeWheel(
  e: WheelEventLike,
  opts: { readonly os: Os; readonly sensitivity: number; readonly pageHeightPx?: number },
): WheelInfo {
  const page = opts.pageHeightPx ?? (typeof window === 'undefined' ? 800 : window.innerHeight || 800);
  let { dx, dy } = wheelDeltaPx(e, page);
  // Windows/Linux browsers may deliver Shift+wheel as a horizontal delta: restore the vertical intent.
  if (opts.os !== 'mac' && e.shiftKey && dy === 0 && dx !== 0) {
    dy = dx;
    dx = 0;
  }
  const notches = dy / WHEEL_NOTCH_PX;
  return {
    dx,
    dy,
    notches,
    beats: notches * opts.sensitivity,
    pinch: e.ctrlKey,
    horizontal: Math.abs(dx) > Math.abs(dy),
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Pointer modifiers (Cmd+click on macOS)
// ---------------------------------------------------------------------------------------------------------------

export interface PointerModifiers {
  /** Toggle selection: Ctrl+click, Cmd+click on macOS. */
  readonly toggle: boolean;
  /** Add to selection / axis lock: Shift. */
  readonly shift: boolean;
  /** Subtract / no snap: Alt (Option). */
  readonly alt: boolean;
  /** Context menu: right button, or Ctrl+click on macOS. */
  readonly contextClick: boolean;
  /**
   * Bypass snapping during a drag: Alt, or Ctrl+Shift (Cmd+Shift on macOS) in the Alt-free variant, where some
   * Linux window managers take Alt+drag (DESIGN.md §8.1).
   */
  readonly noSnap: boolean;
}

export function pointerModifiers(
  e: Pick<MouseEvent, 'button' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'altKey'>,
  os: Os,
  altFree = false,
): PointerModifiers {
  const primary = os === 'mac' ? e.metaKey : e.ctrlKey;
  const macContext = os === 'mac' && e.ctrlKey && e.button === 0;
  return {
    toggle: primary,
    shift: e.shiftKey,
    alt: e.altKey,
    contextClick: e.button === 2 || macContext,
    noSnap: altFree ? primary && e.shiftKey : e.altKey,
  };
}
